import { createFileRoute } from "@tanstack/react-router";
import { Star, MapPin, Clock } from "lucide-react";
import { AdLandingShell, AD_HOURS } from "@/components/ads/AdLandingShell";
import { Button } from "@/components/ui/button";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { reviewsForSalon } from "@/data/reviews";
import herrklippningAsset from "@/assets/opt/herrklippning-tailors-barber.webp";
import { SalonRatingLines } from "@/components/SalonRatingLines";

const heroQuotes = reviewsForSalon("ahlens-city")
  .filter((r) => r.rating === 5 && r.text.length > 40 && r.text.length < 190)
  .slice(0, 3);
const CAMP = "utm_source=google&utm_medium=cpc&utm_campaign=15-rabatt";

const cards = [
  {
    title: "Herrklippning",
    original: "450 kr",
    discounted: "382 kr",
    cta: "Boka Herrklippning",
    href: `https://www.bokadirekt.se/boka-tjanst/tailors-barber-stockholm-ahlens-city-37678/klippning-1247219?${CAMP}`,
  },
  {
    title: "Skäggtrimning",
    original: "380 kr",
    discounted: "323 kr",
    cta: "Boka Skäggtrimning",
    href: `https://www.bokadirekt.se/boka-tjanst/tailors-barber-stockholm-ahlens-city-37678/skagg-trimning-1247227?${CAMP}`,
  },
  {
    title: "Taper Fade / Skin Fade",
    original: "450 kr",
    discounted: "382 kr",
    cta: "Boka Taper Fade",
    href: `https://www.bokadirekt.se/boka-tjanst/tailors-barber-stockholm-ahlens-city-37678/skinfade-taper-fade-2173161?${CAMP}`,
  },
  {
    title: "Hår & Skägg-paket",
    original: "680 kr",
    discounted: "578 kr",
    cta: "Boka Hår & Skägg",
    href: `https://www.bokadirekt.se/boka-tjanst/tailors-barber-stockholm-ahlens-city-37678/klippning-skaggtrimning-1247224?${CAMP}`,
  },
];


export const Route = createFileRoute("/15-rabatt")({
  head: () => ({
    meta: [
      { title: "15% rabatt på din första behandling | Tailor's Barber Åhléns City" },
      { name: "description", content: "Ny kund? Få 15% rabatt på din första behandling hos Tailor's Barber Åhléns City — klippning, skägg eller fade. Boka direkt." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: DiscountPage,
});

function DiscountPage() {
  const primaryHref = cards[0].href;
  return (
    <AdLandingShell stickyCtaHref={primaryHref} stickyCtaLabel="Boka med 15% rabatt →">
      {/* HERO */}
      <section className="container-page pt-10 md:pt-16 pb-10 md:pb-14">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <div className="eyebrow">Exklusivt för nya kunder</div>
            <div className="mt-6 text-[7rem] md:text-[10rem] font-medium leading-none text-brass-deep tracking-tighter font-[family-name:var(--font-times)]">
              15%
            </div>
            <h1 className="mt-2 text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              rabatt på din första behandling
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Exklusivt för nya kunder hos Tailor&apos;s Barber Åhléns City
            </p>
            <div className="mt-5 flex items-start justify-center gap-2 text-sm text-foreground/70 lg:justify-start">
              <span aria-hidden="true">⭐</span>
              <SalonRatingLines salon="ahlens-city" />
              <span>Åhléns City, Stockholm</span>
            </div>
            <ul className="mt-5 flex flex-col gap-3 text-left max-w-xl mx-auto lg:mx-0">
              {heroQuotes.map((q) => (
                <li key={q.author} className="rounded-xl border border-border bg-card/60 px-4 py-3">
                  <div className="flex items-center gap-0.5 text-brass" role="img" aria-label="5 av 5 stjärnor">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={13} className="fill-brass" />
                    ))}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">&ldquo;{q.text}&rdquo;</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {q.author} · {q.source}
                  </p>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Button asChild size="lg">
                <a href={primaryHref} target="_blank" rel="noopener noreferrer">
                  Boka med 15% rabatt →
                </a>
              </Button>
            </div>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted shadow-soft order-first lg:order-last">
            <img
              src={herrklippningAsset}
              alt="Herrklippning hos Tailor's Barber Åhléns City Stockholm"
              loading="eager"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>


      {/* SERVICE CARDS */}
      <section className="container-page py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="eyebrow">Behandlingar</div>
            <h2 className="mt-3 text-3xl md:text-4xl">Välj din behandling</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {cards.map((c) => (
              <div key={c.title} className="card-elev p-6 flex flex-col">
                <h3 className="text-xl">{c.title}</h3>
                <div className="mt-3 flex items-baseline gap-3 mb-6">
                  <span className="text-foreground/50 line-through text-base">{c.original}</span>
                  <span className="text-3xl font-medium text-foreground font-[family-name:var(--font-times)]">
                    {c.discounted}
                  </span>
                  <span className="inline-flex items-center bg-brass text-ink text-xs font-semibold px-2 py-0.5 rounded-full">
                    -15%
                  </span>
                </div>
                <div className="mt-auto">
                  <Button asChild className="w-full">
                    <a href={c.href} target="_blank" rel="noopener noreferrer">
                      {c.cta}
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-6 grid grid-cols-3 gap-3 text-center max-w-3xl mx-auto">
          <div className="flex flex-col items-center gap-1.5">
            <Star size={18} className="fill-brass text-brass" />
            <span className="text-xs md:text-sm font-medium">4,8 rating</span>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <Clock size={18} className="text-brass-deep" />
            <span className="text-xs md:text-sm font-medium">Öppet 7 dagar/v</span>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <MapPin size={18} className="text-brass-deep" />
            <span className="text-xs md:text-sm font-medium">60 sek från T-Centralen</span>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="container-page py-14 md:py-20">
        <div className="max-w-5xl mx-auto">
          <ReviewsBlock salon="ahlens-city" />
        </div>
      </section>

      {/* OPENING HOURS */}
      <section className="bg-secondary/40 border-y border-border py-10">
        <div className="container-page text-center max-w-2xl mx-auto">
          <div className="eyebrow">Öppettider</div>
          <p className="mt-3 text-base text-foreground/80">{AD_HOURS}</p>
          <p className="mt-1 text-sm text-foreground/60">Klarabergsgatan 50, plan 3 · Stockholm</p>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="container-page py-10">
        <p className="mx-auto max-w-2xl text-xs text-muted-foreground text-center leading-relaxed">
          Rabatten gäller din första bokning hos Tailor&apos;s Barber Åhléns City. Gäller ej i kombination
          med andra erbjudanden. Rabatten appliceras av barberaren i salongen.
        </p>
      </section>
    </AdLandingShell>
  );
}
